import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

export const DownloadGuideDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg bg-[var(--surface)] border border-[var(--border-default)] p-0" style={{ borderRadius: 0 }}>
      <DialogHeader className="p-6 pb-4 border-b border-[var(--border-default)]">
        <DialogTitle className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Download VAULT
        </DialogTitle>
        <DialogDescription className="text-sm text-[var(--text-secondary)]">
          Install VAULT on your device for quick access
        </DialogDescription>
      </DialogHeader>
      <div className="p-6 space-y-6">
        <div>
          <h3 className="font-bold mb-2 flex items-center gap-2"><span className="text-lg">iPhone / iPad</span></h3>
          <ol className="text-sm text-[var(--text-secondary)] space-y-2 list-decimal list-inside">
            <li>Open VAULT in <strong>Safari</strong> (required)</li>
            <li>Tap the <strong>Share</strong> button (square with arrow)</li>
            <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
            <li>Tap <strong>"Add"</strong> to confirm</li>
          </ol>
        </div>
        <div className="border-t border-[var(--border-subtle)] pt-4">
          <h3 className="font-bold mb-2"><span className="text-lg">Android</span></h3>
          <ol className="text-sm text-[var(--text-secondary)] space-y-2 list-decimal list-inside">
            <li>Open VAULT in <strong>Chrome</strong></li>
            <li>Tap the <strong>three-dot menu</strong> (top right)</li>
            <li>Tap <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong></li>
            <li>Tap <strong>"Install"</strong> to confirm</li>
          </ol>
        </div>
        <div className="border-t border-[var(--border-subtle)] pt-4">
          <h3 className="font-bold mb-2"><span className="text-lg">Desktop (Chrome/Edge)</span></h3>
          <ol className="text-sm text-[var(--text-secondary)] space-y-2 list-decimal list-inside">
            <li>Look for the <strong>install icon</strong> in the address bar (right side)</li>
            <li>Click <strong>"Install"</strong> in the popup</li>
            <li>VAULT opens as a standalone app window</li>
          </ol>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const HelpDialog = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg bg-[var(--surface)] border border-[var(--border-default)] p-0 max-h-[85vh] overflow-y-auto" style={{ borderRadius: 0 }}>
      <DialogHeader className="p-6 pb-4 border-b border-[var(--border-default)]">
        <DialogTitle className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Help & Tutorials
        </DialogTitle>
        <DialogDescription className="text-sm text-[var(--text-secondary)]">
          Everything you need to know about VAULT
        </DialogDescription>
      </DialogHeader>
      <div className="p-6 space-y-6">
        <div>
          <h3 className="font-bold mb-2 text-[var(--secondary)]">Getting Started</h3>
          <ul className="text-sm text-[var(--text-secondary)] space-y-2">
            <li><strong>Create an Idea:</strong> Click the "+ New Idea" button or press <kbd className="px-1.5 py-0.5 bg-[var(--surface-alt)] border border-[var(--border-default)] text-xs font-mono">Ctrl+N</kbd></li>
            <li><strong>Search:</strong> Use the search bar or press <kbd className="px-1.5 py-0.5 bg-[var(--surface-alt)] border border-[var(--border-default)] text-xs font-mono">Ctrl+K</kbd></li>
            <li><strong>Categories:</strong> Organize ideas by Work, Personal, Business, Creative, Technical</li>
            <li><strong>Tags:</strong> Add custom tags when creating ideas for flexible filtering</li>
          </ul>
        </div>
        <div className="border-t border-[var(--border-subtle)] pt-4">
          <h3 className="font-bold mb-2 text-[var(--secondary)]">AI Enhancement</h3>
          <ul className="text-sm text-[var(--text-secondary)] space-y-2">
            <li><strong>How it works:</strong> Click "AI Enhance" on any idea card</li>
            <li><strong>What it does:</strong> GPT-5.2 expands your idea with details, insights, and action items</li>
            <li><strong>Free plan:</strong> 20 enhancements/month | <strong>Pro:</strong> Unlimited</li>
          </ul>
        </div>
        <div className="border-t border-[var(--border-subtle)] pt-4">
          <h3 className="font-bold mb-2 text-[var(--secondary)]">Templates</h3>
          <ul className="text-sm text-[var(--text-secondary)] space-y-2">
            <li>Use "From Template" to start with structured formats</li>
            <li>Available: Project, Feature Request, Research, Business, Creative, Improvement</li>
          </ul>
        </div>
        <div className="border-t border-[var(--border-subtle)] pt-4">
          <h3 className="font-bold mb-2 text-[var(--secondary)]">Teams (Business Plan)</h3>
          <ul className="text-sm text-[var(--text-secondary)] space-y-2">
            <li><strong>Create a team:</strong> Go to Teams page from the sidebar</li>
            <li><strong>Invite members:</strong> By email (existing users auto-join, new users get an invite)</li>
            <li><strong>Share ideas:</strong> Share individual ideas with your team boards</li>
            <li><strong>Roles:</strong> Owner (full control), Editor (can share), Viewer (read only)</li>
          </ul>
        </div>
        <div className="border-t border-[var(--border-subtle)] pt-4">
          <h3 className="font-bold mb-2 text-[var(--secondary)]">Keyboard Shortcuts</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <kbd className="px-1.5 py-0.5 bg-[var(--surface-alt)] border border-[var(--border-default)] text-xs font-mono">Ctrl+N</kbd>
              <span className="text-[var(--text-secondary)]">New Idea</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-1.5 py-0.5 bg-[var(--surface-alt)] border border-[var(--border-default)] text-xs font-mono">Ctrl+K</kbd>
              <span className="text-[var(--text-secondary)]">Search</span>
            </div>
          </div>
        </div>
        <div className="border-t border-[var(--border-subtle)] pt-4">
          <h3 className="font-bold mb-2 text-[var(--secondary)]">Subscriptions</h3>
          <ul className="text-sm text-[var(--text-secondary)] space-y-2">
            <li><strong>Free:</strong> Unlimited ideas, 20 AI/month, basic features</li>
            <li><strong>Pro ($4.99/mo):</strong> Unlimited everything</li>
            <li><strong>Business ($19.99/mo):</strong> Pro + team collaboration</li>
            <li><strong>Founding Member ($49):</strong> Lifetime Pro access</li>
          </ul>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
